// Herramientas interactivas del widget: todo lo que necesita JS además de CSS.
// Cada herramienta expone { start, stop } y se activa desde el panel.
// Reglas: no dependencias, listeners siempre retirables, nunca romper el sitio.

import { LOCALES, type Lang, type Strings } from "./i18n";

export interface ToolContext {
  shadow: ShadowRoot;
  host: HTMLElement;
  t: () => Strings; // strings del idioma actual
  lang: () => string; // uno de los 10 idiomas del widget
  color: string; // color de marca del cliente
  api: string; // base de la API (para las funciones con IA)
}

// Llama a la IA del backend (la clave nunca viaja al navegador).
// Devuelve null si falla o tarda: la IA es mejora progresiva, nunca dependencia.
async function askAI(
  ctx: ToolContext,
  endpoint: "summary" | "simplify",
  text: string
): Promise<string | null> {
  try {
    const res = await fetch(`${ctx.api}/ai/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: text.slice(0, 8000), lang: ctx.lang() }),
      signal: AbortSignal.timeout(50_000),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data.result === "string" ? data.result : null;
  } catch {
    return null;
  }
}

interface Tool {
  start(): void;
  stop(): void;
}

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((ev: any) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
};

// ---------- utilidades ----------

function isInPage(ctx: ToolContext, el: Element | null): el is HTMLElement {
  return !!el && el instanceof HTMLElement && !ctx.host.contains(el);
}

function isEditable(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return (
    /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable === true
  );
}

function speak(text: string, lang: string, rate = 1): void {
  try {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text.slice(0, 1500));
    // El idioma elegido en el panel manda: si el visitante puso el widget en
    // inglés, se lee en inglés aunque la página declare otro idioma.
    u.lang = LOCALES[lang as Lang] ?? lang ?? "es-ES";
    u.rate = rate;
    window.speechSynthesis.speak(u);
  } catch {
    /* sin voz disponible */
  }
}

function stopSpeaking(): void {
  try {
    window.speechSynthesis.cancel();
  } catch {
    /* ignorar */
  }
}

function textOf(el: HTMLElement): string {
  return (el.innerText || el.textContent || "").replace(/\s+/g, " ").trim();
}

function accessibleName(el: HTMLElement): string {
  return (
    el.getAttribute("aria-label") ||
    (el as HTMLImageElement).alt ||
    el.getAttribute("title") ||
    textOf(el)
  );
}

function focusTarget(el: HTMLElement): void {
  if (!el.hasAttribute("tabindex") && el.tabIndex < 0) el.setAttribute("tabindex", "-1");
  el.scrollIntoView({ block: "center" });
  el.focus({ preventScroll: true });
}

// Diálogo genérico dentro del shadow (aislado del CSS del sitio).
function openDialog(ctx: ToolContext, title: string, build: (body: HTMLElement) => void): void {
  ctx.shadow.querySelector(".a11y-dialog")?.remove();
  const wrap = document.createElement("div");
  wrap.className = "a11y-dialog";
  wrap.innerHTML = `
    <div class="a11y-dialog-box" role="dialog" aria-label="${title}">
      <div class="a11y-dialog-head"><span>${title}</span><button type="button" aria-label="${ctx.t().close}">&times;</button></div>
      <div class="a11y-dialog-body"></div>
    </div>`;
  wrap.addEventListener("click", (e) => {
    if (e.target === wrap) wrap.remove();
  });
  (wrap.querySelector(".a11y-dialog-head button") as HTMLElement).addEventListener(
    "click",
    () => wrap.remove()
  );
  build(wrap.querySelector(".a11y-dialog-body") as HTMLElement);
  ctx.shadow.appendChild(wrap);
}

// ---------- herramientas ----------

// Lector de texto: clic sobre cualquier texto para escucharlo.
function ttsTool(ctx: ToolContext): Tool {
  let last: HTMLElement | null = null;
  const onClick = (e: MouseEvent) => {
    const el = e.target as Element | null;
    if (!isInPage(ctx, el)) return;
    const block = el.closest("p,li,h1,h2,h3,h4,h5,h6,td,th,blockquote,label,figcaption,a,button,span,div");
    if (!block || !isInPage(ctx, block)) return;
    const text = textOf(block as HTMLElement);
    if (!text) return;
    // Evitamos navegar mientras se lee un enlace.
    if ((el as HTMLElement).closest("a")) e.preventDefault();
    last?.style.removeProperty("outline");
    last = block as HTMLElement;
    last.style.setProperty("outline", "3px solid #f59e0b", "important");
    speak(text, ctx.lang());
  };
  return {
    start() {
      if (!("speechSynthesis" in window)) {
        alertChip(ctx, ctx.t().ttsUnsupported);
        return;
      }
      alertChip(ctx, ctx.t().ttsHint);
      document.addEventListener("click", onClick, true);
    },
    stop() {
      document.removeEventListener("click", onClick, true);
      last?.style.removeProperty("outline");
      last = null;
      stopSpeaking();
    },
  };
}

// Lectura al enfocar: anuncia el elemento que recibe foco o hover.
function srModeTool(ctx: ToolContext): Tool {
  let timer = 0;
  const onFocus = (e: FocusEvent) => {
    const el = e.target as Element | null;
    if (!isInPage(ctx, el)) return;
    const name = accessibleName(el);
    if (name) speak(name, ctx.lang(), 1.1);
  };
  const onOver = (e: MouseEvent) => {
    const el = e.target as Element | null;
    if (!isInPage(ctx, el)) return;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      const name = accessibleName(el);
      if (name && name.length < 240) speak(name, ctx.lang(), 1.1);
    }, 350);
  };
  return {
    start() {
      document.addEventListener("focusin", onFocus);
      document.addEventListener("mouseover", onOver);
    },
    stop() {
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("mouseover", onOver);
      window.clearTimeout(timer);
      stopSpeaking();
    },
  };
}

// Comandos de voz: bajar, subir, inicio, fin, leer, parar (es/en).
function voiceTool(ctx: ToolContext): Tool {
  let rec: SpeechRecognitionLike | null = null;
  let active = false;
  // Palabras clave en los 10 idiomas del widget.
  const CMD = {
    stop: /(parar|stop|stopp|arrête|arret|pare|стоп|توقف|停止|止まれ|ferma)/,
    top: /(inicio|top|début|debut|anfang|início|inizio|顶部|先頭|начало|البداية)/,
    bottom: /(final|fin\b|bottom|ende|fim|fine|底部|末尾|конец|النهاية)/,
    down: /(bajar|abajo|down|descend|runter|desce|giù|giu|下|вниз|انزل)/,
    up: /(subir|arriba|\bup\b|monte|hoch|sobe|\bsu\b|上|вверх|اطلع)/,
    read: /(leer|read|lire|lis|lesen|\bler\b|leggi|朗读|読む|читать|اقرأ)/,
    back: /(atrás|atras|back|retour|zurück|voltar|indietro|返回|戻る|назад|رجوع)/,
  };
  const run = (cmd: string) => {
    const c = cmd.toLowerCase();
    if (CMD.stop.test(c)) stopSpeaking();
    else if (CMD.top.test(c)) window.scrollTo({ top: 0, behavior: "smooth" });
    else if (CMD.bottom.test(c))
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    else if (CMD.down.test(c)) window.scrollBy({ top: 450, behavior: "smooth" });
    else if (CMD.up.test(c)) window.scrollBy({ top: -450, behavior: "smooth" });
    else if (CMD.read.test(c)) speak(textOf(document.body).slice(0, 1500), ctx.lang());
    else if (CMD.back.test(c)) history.back();
  };
  return {
    start() {
      const Ctor =
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (!Ctor) {
        alertChip(ctx, ctx.t().voiceUnsupported);
        return;
      }
      active = true;
      rec = new Ctor() as SpeechRecognitionLike;
      rec.lang = LOCALES[ctx.lang() as Lang] ?? "es-ES";
      rec.continuous = true;
      rec.interimResults = false;
      rec.onresult = (ev: any) => {
        try {
          const r = ev.results[ev.results.length - 1];
          if (r?.isFinal) run(r[0].transcript);
        } catch {
          /* ignorar resultados raros */
        }
      };
      rec.onend = () => {
        if (active && rec) {
          try {
            rec.start(); // reconexión: el navegador corta la escucha solo
          } catch {
            /* ignorar */
          }
        }
      };
      try {
        rec.start();
        alertChip(ctx, ctx.t().voiceOn, 6000);
      } catch {
        alertChip(ctx, ctx.t().voiceUnsupported);
      }
    },
    stop() {
      active = false;
      try {
        rec?.stop();
      } catch {
        /* ignorar */
      }
      rec = null;
    },
  };
}

// Navegación por teclado: H encabezados, L enlaces, B botones, G regiones.
function keyboardNavTool(ctx: ToolContext): Tool {
  const cycles: Record<string, { sel: string; i: number }> = {
    h: { sel: "h1,h2,h3,h4,h5,h6", i: -1 },
    l: { sel: "a[href]", i: -1 },
    b: { sel: "button,[role=button],input[type=submit],input[type=button]", i: -1 },
    g: { sel: "main,nav,header,footer,aside,form,[role=main],[role=navigation],[role=search]", i: -1 },
  };
  const onKey = (e: KeyboardEvent) => {
    if (isEditable(e.target) || e.ctrlKey || e.altKey || e.metaKey) return;
    const c = cycles[e.key.toLowerCase()];
    if (!c) return;
    const all = Array.from(document.querySelectorAll<HTMLElement>(c.sel)).filter(
      (el) => isInPage(ctx, el) && el.offsetParent !== null
    );
    if (!all.length) return;
    c.i = e.shiftKey ? (c.i - 1 + all.length) % all.length : (c.i + 1) % all.length;
    focusTarget(all[c.i]);
    e.preventDefault();
  };
  return {
    start() {
      document.addEventListener("keydown", onKey, true);
      alertChip(ctx, ctx.t().kbdHint, 6000);
    },
    stop() {
      document.removeEventListener("keydown", onKey, true);
    },
  };
}

// Navegación inteligente: flechas para moverse entre elementos interactivos.
function smartNavTool(ctx: ToolContext): Tool {
  const SEL = "a[href],button,input,select,textarea,[role=button],[tabindex]:not([tabindex='-1'])";
  const onKey = (e: KeyboardEvent) => {
    if (isEditable(e.target) || e.ctrlKey || e.altKey || e.metaKey) return;
    if (!/^Arrow(Up|Down|Left|Right)$/.test(e.key)) return;
    const all = Array.from(document.querySelectorAll<HTMLElement>(SEL)).filter(
      (el) => isInPage(ctx, el) && el.offsetParent !== null
    );
    if (!all.length) return;
    const current = document.activeElement as HTMLElement | null;
    let i = current ? all.indexOf(current) : -1;
    const fwd = e.key === "ArrowDown" || e.key === "ArrowRight";
    i = i < 0 ? 0 : (i + (fwd ? 1 : -1) + all.length) % all.length;
    focusTarget(all[i]);
    e.preventDefault();
  };
  return {
    start() {
      document.addEventListener("keydown", onKey, true);
      alertChip(ctx, ctx.t().smartHint, 6000);
    },
    stop() {
      document.removeEventListener("keydown", onKey, true);
    },
  };
}

// Cuadrícula de ratón: clic sin ratón, refinando celdas 1-9 con el teclado.
function mousegridTool(ctx: ToolContext): Tool {
  let overlay: HTMLElement | null = null;
  let rect = { x: 0, y: 0, w: 0, h: 0 };
  const draw = () => {
    if (!overlay) return;
    overlay.innerHTML = "";
    for (let n = 0; n < 9; n++) {
      const cell = document.createElement("div");
      const cw = rect.w / 3;
      const ch = rect.h / 3;
      cell.className = "a11y-grid-cell";
      cell.style.cssText = `left:${rect.x + (n % 3) * cw}px;top:${rect.y + Math.floor(n / 3) * ch}px;width:${cw}px;height:${ch}px;`;
      cell.textContent = String(n + 1);
      overlay.appendChild(cell);
    }
    const hint = document.createElement("div");
    hint.className = "a11y-grid-hint";
    hint.textContent = ctx.t().gridHint;
    overlay.appendChild(hint);
  };
  const clickCenter = () => {
    const cx = rect.x + rect.w / 2;
    const cy = rect.y + rect.h / 2;
    stop();
    const el = document.elementFromPoint(cx, cy) as HTMLElement | null;
    if (el && !ctx.host.contains(el)) {
      el.focus?.();
      el.click?.();
    }
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      stop();
      e.preventDefault();
      return;
    }
    if (e.key === "Enter") {
      clickCenter();
      e.preventDefault();
      return;
    }
    const n = parseInt(e.key, 10);
    if (n >= 1 && n <= 9) {
      const cw = rect.w / 3;
      const ch = rect.h / 3;
      rect = {
        x: rect.x + ((n - 1) % 3) * cw,
        y: rect.y + Math.floor((n - 1) / 3) * ch,
        w: cw,
        h: ch,
      };
      if (rect.w < 60 || rect.h < 60) clickCenter();
      else draw();
      e.preventDefault();
    }
  };
  function start() {
    stop();
    rect = { x: 0, y: 0, w: window.innerWidth, h: window.innerHeight };
    overlay = document.createElement("div");
    overlay.className = "a11y-grid";
    ctx.shadow.appendChild(overlay);
    draw();
    document.addEventListener("keydown", onKey, true);
  }
  function stop() {
    overlay?.remove();
    overlay = null;
    document.removeEventListener("keydown", onKey, true);
  }
  return { start, stop };
}

// Guía de lectura: regla horizontal que sigue al puntero.
function readGuideTool(ctx: ToolContext): Tool {
  let bar: HTMLElement | null = null;
  const onMove = (e: MouseEvent) => {
    if (bar) bar.style.top = e.clientY + 14 + "px";
  };
  return {
    start() {
      bar = document.createElement("div");
      bar.className = "a11y-guide";
      ctx.shadow.appendChild(bar);
      document.addEventListener("mousemove", onMove);
    },
    stop() {
      document.removeEventListener("mousemove", onMove);
      bar?.remove();
      bar = null;
    },
  };
}

// Foco de lectura: oscurece todo menos una banda alrededor del puntero.
function readFocusTool(ctx: ToolContext): Tool {
  let top: HTMLElement | null = null;
  let bottom: HTMLElement | null = null;
  const BAND = 130;
  const onMove = (e: MouseEvent) => {
    if (!top || !bottom) return;
    top.style.height = Math.max(0, e.clientY - BAND / 2) + "px";
    bottom.style.top = e.clientY + BAND / 2 + "px";
  };
  return {
    start() {
      top = document.createElement("div");
      top.className = "a11y-mask a11y-mask-top";
      bottom = document.createElement("div");
      bottom.className = "a11y-mask a11y-mask-bottom";
      ctx.shadow.appendChild(top);
      ctx.shadow.appendChild(bottom);
      document.addEventListener("mousemove", onMove);
    },
    stop() {
      document.removeEventListener("mousemove", onMove);
      top?.remove();
      bottom?.remove();
      top = bottom = null;
    },
  };
}

// Lupa: lente junto al cursor con el texto del elemento apuntado, ampliado.
function magnifierTool(ctx: ToolContext): Tool {
  let lens: HTMLElement | null = null;
  const onMove = (e: MouseEvent) => {
    if (!lens) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    let text = "";
    if (isInPage(ctx, el)) {
      const block = el.closest("p,li,h1,h2,h3,h4,h5,h6,td,th,a,button,label,span,figcaption");
      if (block && isInPage(ctx, block)) text = textOf(block as HTMLElement).slice(0, 220);
    }
    if (!text) {
      lens.style.display = "none";
      return;
    }
    lens.style.display = "block";
    lens.textContent = text;
    const x = Math.min(e.clientX + 20, window.innerWidth - 340);
    const y = Math.min(e.clientY + 24, window.innerHeight - 160);
    lens.style.left = x + "px";
    lens.style.top = y + "px";
  };
  return {
    start() {
      lens = document.createElement("div");
      lens.className = "a11y-lens";
      ctx.shadow.appendChild(lens);
      document.addEventListener("mousemove", onMove);
    },
    stop() {
      document.removeEventListener("mousemove", onMove);
      lens?.remove();
      lens = null;
    },
  };
}

// Ampliador de texto: barra fija inferior con el texto apuntado, en grande.
function textMagTool(ctx: ToolContext): Tool {
  let bar: HTMLElement | null = null;
  const onMove = (e: MouseEvent) => {
    if (!bar) return;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    if (!isInPage(ctx, el)) return;
    const block = el.closest("p,li,h1,h2,h3,h4,h5,h6,td,th,a,button,label,figcaption");
    if (!block || !isInPage(ctx, block)) return;
    const text = textOf(block as HTMLElement);
    if (text) {
      bar.textContent = text.slice(0, 300);
      bar.style.display = "block";
    }
  };
  return {
    start() {
      bar = document.createElement("div");
      bar.className = "a11y-textmag";
      ctx.shadow.appendChild(bar);
      document.addEventListener("mousemove", onMove);
    },
    stop() {
      document.removeEventListener("mousemove", onMove);
      bar?.remove();
      bar = null;
    },
  };
}

// Descripción de imágenes: superpone el texto alt de cada imagen visible.
function imgDescTool(ctx: ToolContext): Tool {
  let layer: HTMLElement | null = null;
  let raf = 0;
  const update = () => {
    if (!layer) return;
    layer.innerHTML = "";
    const imgs = Array.from(document.images).filter((img) => isInPage(ctx, img));
    for (const img of imgs) {
      const r = img.getBoundingClientRect();
      if (r.width < 40 || r.height < 40 || r.bottom < 0 || r.top > window.innerHeight) continue;
      const badge = document.createElement("div");
      badge.className = "a11y-imgdesc" + (img.alt ? "" : " missing");
      badge.textContent = img.alt || ctx.t().noDesc;
      badge.style.left = r.left + "px";
      badge.style.top = Math.max(0, r.bottom - 26) + "px";
      badge.style.maxWidth = Math.max(60, r.width) + "px";
      layer.appendChild(badge);
    }
  };
  const schedule = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(update);
  };
  return {
    start() {
      layer = document.createElement("div");
      layer.className = "a11y-imgdesc-layer";
      ctx.shadow.appendChild(layer);
      update();
      window.addEventListener("scroll", schedule, true);
      window.addEventListener("resize", schedule);
    },
    stop() {
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      cancelAnimationFrame(raf);
      layer?.remove();
      layer = null;
    },
  };
}

// Silenciar medios: pausa y silencia audio/video, incluidos los que aparezcan luego.
function muteMediaTool(ctx: ToolContext): Tool {
  let observer: MutationObserver | null = null;
  const muted = new Set<HTMLMediaElement>();
  const silence = (m: HTMLMediaElement) => {
    try {
      m.pause();
      m.muted = true;
      m.autoplay = false;
      muted.add(m);
    } catch {
      /* ignorar */
    }
  };
  const sweep = () => document.querySelectorAll("video,audio").forEach((m) => silence(m as HTMLMediaElement));
  return {
    start() {
      sweep();
      observer = new MutationObserver(sweep);
      observer.observe(document.body, { childList: true, subtree: true });
    },
    stop() {
      observer?.disconnect();
      observer = null;
      muted.forEach((m) => {
        try {
          m.muted = false;
        } catch {
          /* ignorar */
        }
      });
      muted.clear();
    },
  };
}

// Diccionario: selecciona una palabra y busca su definición (Wikipedia REST).
function dictTool(ctx: ToolContext): Tool {
  let pop: HTMLElement | null = null;
  const close = () => {
    pop?.remove();
    pop = null;
  };
  const onUp = () => {
    window.setTimeout(async () => {
      const sel = document.getSelection();
      const term = sel?.toString().trim() ?? "";
      if (!term || term.length > 60 || term.split(/\s+/).length > 3) return;
      const range = sel!.rangeCount ? sel!.getRangeAt(0).getBoundingClientRect() : null;
      close();
      pop = document.createElement("div");
      pop.className = "a11y-dict";
      pop.style.left = Math.min(range?.left ?? 40, window.innerWidth - 340) + "px";
      pop.style.top = Math.min((range?.bottom ?? 40) + 10, window.innerHeight - 200) + "px";
      pop.innerHTML = `<strong>${term}</strong><p>${ctx.t().dictSearching}</p>`;
      ctx.shadow.appendChild(pop);
      try {
        const res = await fetch(
          `https://${ctx.lang()}.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(term)}`
        );
        const data = res.ok ? await res.json() : null;
        const def = data?.extract as string | undefined;
        if (pop) {
          pop.innerHTML = `<strong>${term}</strong><p>${def ? def.slice(0, 400) : ctx.t().dictNotFound}</p>`;
        }
      } catch {
        if (pop) pop.innerHTML = `<strong>${term}</strong><p>${ctx.t().dictNotFound}</p>`;
      }
    }, 10);
  };
  return {
    start() {
      alertChip(ctx, ctx.t().dictHint, 5000);
      document.addEventListener("mouseup", onUp);
    },
    stop() {
      document.removeEventListener("mouseup", onUp);
      close();
    },
  };
}

// Teclado virtual: teclado en pantalla que escribe en el campo activo del sitio.
function vkbTool(ctx: ToolContext): Tool {
  let board: HTMLElement | null = null;
  let target: HTMLElement | null = null;
  let shift = false;
  const ROWS = ["1234567890", "qwertyuiop", "asdfghjklñ", "zxcvbnm"];
  const onFocus = (e: FocusEvent) => {
    const el = e.target as HTMLElement | null;
    if (el && isInPage(ctx, el) && (isEditable(el) || el.isContentEditable)) target = el;
  };
  const type = (ch: string) => {
    if (!target) return;
    try {
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        const start = target.selectionStart ?? target.value.length;
        const end = target.selectionEnd ?? target.value.length;
        if (ch === "\b") {
          if (start === end && start > 0)
            target.value = target.value.slice(0, start - 1) + target.value.slice(end);
          else target.value = target.value.slice(0, start) + target.value.slice(end);
          target.selectionStart = target.selectionEnd = Math.max(0, start - (start === end ? 1 : 0));
        } else {
          target.value = target.value.slice(0, start) + ch + target.value.slice(end);
          target.selectionStart = target.selectionEnd = start + ch.length;
        }
        target.dispatchEvent(new Event("input", { bubbles: true }));
      } else if (target.isContentEditable) {
        if (ch === "\b") document.execCommand("delete");
        else document.execCommand("insertText", false, ch);
      }
      target.focus();
    } catch {
      /* ignorar */
    }
  };
  const build = () => {
    board = document.createElement("div");
    board.className = "a11y-vkb";
    for (const row of ROWS) {
      const r = document.createElement("div");
      r.className = "a11y-vkb-row";
      for (const ch of row) {
        const k = document.createElement("button");
        k.type = "button";
        k.textContent = ch;
        k.addEventListener("mousedown", (e) => e.preventDefault());
        k.addEventListener("click", () => type(shift ? ch.toUpperCase() : ch));
        r.appendChild(k);
      }
      board.appendChild(r);
    }
    const extra = document.createElement("div");
    extra.className = "a11y-vkb-row";
    const mk = (label: string, fn: () => void, wide = false) => {
      const k = document.createElement("button");
      k.type = "button";
      k.textContent = label;
      if (wide) k.classList.add("wide");
      k.addEventListener("mousedown", (e) => e.preventDefault());
      k.addEventListener("click", fn);
      extra.appendChild(k);
    };
    mk("⇧", () => {
      shift = !shift;
      board?.classList.toggle("shift", shift);
    });
    mk("␣", () => type(" "), true);
    mk("⌫", () => type("\b"));
    mk("↵", () => {
      if (target)
        target.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", bubbles: true }));
    });
    board.appendChild(extra);
    ctx.shadow.appendChild(board);
  };
  return {
    start() {
      build();
      document.addEventListener("focusin", onFocus);
      const active = document.activeElement as HTMLElement | null;
      if (active && isInPage(ctx, active) && isEditable(active)) target = active;
    },
    stop() {
      document.removeEventListener("focusin", onFocus);
      board?.remove();
      board = null;
      target = null;
    },
  };
}

// ---------- acciones de un solo uso (diálogos) ----------

export function showPageStructure(ctx: ToolContext): void {
  const t = ctx.t();
  openDialog(ctx, t.pageStructure, (body) => {
    const tabs: { label: string; sel: string; name: (el: HTMLElement) => string }[] = [
      { label: t.structHeadings, sel: "h1,h2,h3,h4,h5,h6", name: (el) => `${el.tagName} · ${textOf(el).slice(0, 80)}` },
      { label: t.structLandmarks, sel: "main,nav,header,footer,aside,form,section[aria-label]", name: (el) => el.tagName.toLowerCase() + (el.getAttribute("aria-label") ? ` · ${el.getAttribute("aria-label")}` : "") },
      { label: t.structLinks, sel: "a[href]", name: (el) => textOf(el).slice(0, 80) || (el as HTMLAnchorElement).href },
    ];
    const bar = document.createElement("div");
    bar.className = "a11y-tabs";
    const list = document.createElement("div");
    list.className = "a11y-list";
    const show = (idx: number) => {
      bar.querySelectorAll("button").forEach((b, i) => b.classList.toggle("on", i === idx));
      list.innerHTML = "";
      const items = Array.from(document.querySelectorAll<HTMLElement>(tabs[idx].sel)).filter(
        (el) => isInPage(ctx, el)
      );
      if (!items.length) {
        list.innerHTML = `<p class="a11y-empty">${t.emptyList}</p>`;
        return;
      }
      for (const el of items.slice(0, 200)) {
        const label = tabs[idx].name(el).trim();
        if (!label) continue;
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = label;
        b.addEventListener("click", () => focusTarget(el));
        list.appendChild(b);
      }
    };
    tabs.forEach((tab, i) => {
      const b = document.createElement("button");
      b.type = "button";
      b.textContent = tab.label;
      b.addEventListener("click", () => show(i));
      bar.appendChild(b);
    });
    body.appendChild(bar);
    body.appendChild(list);
    show(0);
  });
}

export function showPageSummary(ctx: ToolContext): void {
  const t = ctx.t();
  openDialog(ctx, t.summaryTitle, (body) => {
    // 1. Resumen local inmediato (encabezados + primer párrafo).
    const list = document.createElement("div");
    list.className = "a11y-summary";
    const heads = Array.from(document.querySelectorAll<HTMLElement>("h1,h2,h3")).filter((el) =>
      isInPage(ctx, el)
    );
    if (!heads.length) {
      const main = document.querySelector("main,article") ?? document.body;
      list.innerHTML = `<p>${textOf(main as HTMLElement).slice(0, 600)}…</p>`;
    } else {
      for (const h of heads.slice(0, 30)) {
        const item = document.createElement("div");
        const title = document.createElement("strong");
        title.textContent = textOf(h).slice(0, 100);
        item.appendChild(title);
        let sib = h.nextElementSibling;
        while (sib && !textOf(sib as HTMLElement)) sib = sib.nextElementSibling;
        if (sib && !/^H[1-6]$/.test(sib.tagName)) {
          const p = document.createElement("p");
          p.textContent = textOf(sib as HTMLElement).slice(0, 180);
          item.appendChild(p);
        }
        list.appendChild(item);
      }
    }
    body.appendChild(list);

    // 2. Mejora progresiva: resumen con IA en lenguaje claro.
    const aiBox = document.createElement("div");
    aiBox.className = "a11y-aibox";
    aiBox.innerHTML = `<strong>${t.aiSummary}</strong><p class="a11y-ailoading">${t.aiLoading}</p>`;
    body.appendChild(aiBox);
    const main = document.querySelector("main,article,[role=main]") ?? document.body;
    askAI(ctx, "summary", textOf(main as HTMLElement)).then((result) => {
      const p = aiBox.querySelector("p");
      if (!p) return;
      if (result) {
        p.classList.remove("a11y-ailoading");
        p.textContent = result;
      } else {
        aiBox.remove(); // sin IA no ensuciamos el diálogo: queda el resumen local
      }
    });
  });
}

// Lectura fácil: reescribe con IA el texto seleccionado (o el principal) en
// lenguaje llano. Si la IA no responde, lo indica con honestidad.
export function showEasyRead(ctx: ToolContext): void {
  const t = ctx.t();
  const selection = document.getSelection()?.toString().trim() ?? "";
  const main = document.querySelector("main,article,[role=main]") ?? document.body;
  const text = selection.length > 40 ? selection : textOf(main as HTMLElement);
  openDialog(ctx, t.easyRead, (body) => {
    body.classList.add("a11y-readable-view");
    const p = document.createElement("p");
    p.className = "a11y-ailoading";
    p.textContent = t.aiLoading;
    body.appendChild(p);
    askAI(ctx, "simplify", text).then((result) => {
      p.classList.remove("a11y-ailoading");
      p.textContent = result ?? t.aiError;
    });
  });
}

export function showReadableMode(ctx: ToolContext): void {
  const t = ctx.t();
  openDialog(ctx, t.readableMode, (body) => {
    body.classList.add("a11y-readable-view");
    const root = document.querySelector("main,article,[role=main]") ?? document.body;
    const parts = Array.from(
      root.querySelectorAll<HTMLElement>("h1,h2,h3,h4,p,li,blockquote")
    ).filter((el) => isInPage(ctx, el));
    let added = 0;
    for (const el of parts) {
      const text = textOf(el);
      if (!text || text.length < 3) continue;
      const node = document.createElement(/^H[1-4]$/.test(el.tagName) ? el.tagName.toLowerCase() : "p");
      node.textContent = text;
      body.appendChild(node);
      if (++added > 400) break;
    }
    if (!added) body.innerHTML = `<p class="a11y-empty">${t.emptyList}</p>`;
  });
}

// ---------- aviso flotante ----------

let chipTimer = 0;
export function alertChip(ctx: ToolContext, msg: string, ms = 3500): void {
  let chip = ctx.shadow.querySelector(".a11y-chip") as HTMLElement | null;
  if (!chip) {
    chip = document.createElement("div");
    chip.className = "a11y-chip";
    ctx.shadow.appendChild(chip);
  }
  chip.textContent = msg;
  chip.style.display = "block";
  window.clearTimeout(chipTimer);
  chipTimer = window.setTimeout(() => {
    if (chip) chip.style.display = "none";
  }, ms);
}

// ---------- registro de herramientas conmutables ----------

export type ToolName =
  | "tts"
  | "srMode"
  | "voice"
  | "keyboardNav"
  | "smartNav"
  | "readGuide"
  | "readFocus"
  | "magnifier"
  | "textMag"
  | "imgDesc"
  | "muteMedia"
  | "dict"
  | "vkb";

export class Tools {
  private tools: Record<ToolName, Tool>;
  private on = new Set<ToolName>();
  readonly mousegrid: Tool;

  constructor(private ctx: ToolContext) {
    this.tools = {
      tts: ttsTool(ctx),
      srMode: srModeTool(ctx),
      voice: voiceTool(ctx),
      keyboardNav: keyboardNavTool(ctx),
      smartNav: smartNavTool(ctx),
      readGuide: readGuideTool(ctx),
      readFocus: readFocusTool(ctx),
      magnifier: magnifierTool(ctx),
      textMag: textMagTool(ctx),
      imgDesc: imgDescTool(ctx),
      muteMedia: muteMediaTool(ctx),
      dict: dictTool(ctx),
      vkb: vkbTool(ctx),
    };
    this.mousegrid = mousegridTool(ctx);
  }

  set(name: ToolName, enabled: boolean): void {
    const active = this.on.has(name);
    if (enabled === active) return;
    try {
      if (enabled) {
        this.tools[name].start();
        this.on.add(name);
      } else {
        this.tools[name].stop();
        this.on.delete(name);
      }
    } catch {
      /* una herramienta jamás debe tumbar el widget */
    }
  }

  stopAll(): void {
    for (const name of Array.from(this.on)) this.set(name, false);
    this.mousegrid.stop();
  }
}
